import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Ventaneo simple sin dependencias externas: solo se montan en el DOM las
 * filas dentro del viewport visible (+ overscan). Se activa únicamente
 * cuando la lista aplanada supera VIRTUALIZE_THRESHOLD (ver constants.js);
 * para el tamaño real de menú de hoy (~26 ítems) nunca se usa, por lo que
 * no agrega riesgo al caso común — solo protege el escenario de un catálogo
 * de módulos que crezca a cientos de ítems.
 */
export function VirtualList({ rows, rowHeight, renderRow, className, overscan = 6 }) {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    setViewportHeight(node.clientHeight);
    const observer = new ResizeObserver((entries) => {
      setViewportHeight(entries[0].contentRect.height);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleScroll = useCallback((event) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  const totalHeight = rows.length * rowHeight;
  const firstIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const visibleCount = Math.ceil(viewportHeight / rowHeight) + overscan * 2;
  const lastIndex = Math.min(rows.length, firstIndex + visibleCount);
  const visibleRows = rows.slice(firstIndex, lastIndex);

  return (
    <div ref={containerRef} onScroll={handleScroll} className={className}>
      <div style={{ height: totalHeight, position: "relative" }}>
        {visibleRows.map((row, offset) => {
          const index = firstIndex + offset;
          return (
            <div
              key={row.item.id}
              style={{ position: "absolute", top: index * rowHeight, left: 0, right: 0, height: rowHeight }}
            >
              {renderRow(row)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
